import { 
  calculatePliseUsedM2, 
  calculateDiscountedPrice, 
  calculateUsedM2, 
  calculatePileDensity,
  calculateCapsulePrice,
  calculateTotalPrice,
  calculateJaluziUsedM2,
  calculateJaluziTotalPrice,
  calculateDikeyPerdeTotalPrice,
  calculateAksesuarTotalPrice,
  calculateKatlamaliPerdeUsedM,
  calculateKatlamaliPerdeTotalPrice,
  calculateTotalCost,
  calculateStorZebraUsedM2,
  calculateStorZebraTotalPrice,
  calculateRustikTotalPrice,
  calculateRaySistemiUsedMeter,
  calculateRustikUsedCm,
  calculateAstarPrice
} from '../../../utils';

export { 
  calculatePliseUsedM2, 
  calculateDiscountedPrice, 
  calculateUsedM2, 
  calculatePileDensity,
  calculateCapsulePrice,
  calculateTotalPrice,
  calculateJaluziUsedM2,
  calculateJaluziTotalPrice,
  calculateDikeyPerdeTotalPrice,
  calculateAksesuarTotalPrice,
  calculateKatlamaliPerdeUsedM,
  calculateKatlamaliPerdeTotalPrice,
  calculateTotalCost,
  calculateStorZebraUsedM2,
  calculateStorZebraTotalPrice,
  calculateRustikTotalPrice,
  calculateRaySistemiUsedMeter,
  calculateRustikUsedCm,
  calculateAstarPrice
};

/**
 * Plise Batch Hesaplama Yardımcı Fonksiyonu
 */
/**
 * Plise satır hesabı:
 * (p1 + [multisistem ? p2 : 0] + [pratikMontaj ? pratik : 0]) * [egim ? 1.5 : 1] * m2 * adet
 */
export const calculatePliseBatchTotals = (
  items,
  p1Price,
  p2Price,
  mounting,
  discount,
  globalQty = 1,
  pratikMontajPrice = 0
) => {
  const gQty = parseFloat(globalQty) || 1;
  const defaultPratik = parseFloat(pratikMontajPrice) || 0;

  if (!Array.isArray(items) || items.length === 0) {
    const mount = (parseFloat(mounting) || 0) * gQty;
    return {
      items: items || [],
      totalPrice: mount > 0 ? mount.toFixed(2) : '0.00',
      discountedPrice: null
    };
  }

  const p1 = parseFloat(p1Price) || 0;
  const p2 = parseFloat(p2Price) || 0;
  const mount = parseFloat(mounting) || 0;
  const disc = parseFloat(discount) || 0;

  let totalRowsSum = 0;
  const updatedItems = items.map(item => {
    const m2 = calculatePliseUsedM2(item.width, item.height);
    const qty = parseFloat(item.quantity) || 1;
    const pratik = item.hasPratikMontaj
      ? (parseFloat(item.pratikMontajAmount) || defaultPratik)
      : 0;
    let unitPrice = p1 + (item.multisistem ? p2 : 0) + pratik;
    if (item.hasEgimFarki) {
      unitPrice *= 1.5;
    }
    const rowTotal = m2 * unitPrice * qty;
    totalRowsSum += rowTotal;
    return {
      ...item,
      usedM2: m2.toFixed(2),
      rowTotal: rowTotal.toFixed(2)
    };
  });

  const finalTotal = (totalRowsSum + mount) * gQty;
  const totalPrice = finalTotal.toFixed(2);
  let discountedPrice = null;

  if (disc > 0) {
    discountedPrice = calculateDiscountedPrice(finalTotal, disc).toFixed(2);
  }

  return {
    items: updatedItems,
    totalPrice,
    discountedPrice
  };
};

/**
 * Boş sipariş state'i döndürür
 */
export const getInitialOrderState = () => ({
  roomName: '',
  productName: '',
  productVariant: '',
  width: '',
  height: '',
  pileModel: '',
  pileDensity: '',
  usedM2: '',
  usedM: '',
  usedMeter: '',
  olcuTamamlama: '',
  unitPrice: '',
  extraPrice: '',
  capsuleUnitPrice: '',
  capsulePrice: '',
  altEtekKatlama: false,
  hasOzelTasarim: false,
  ozelProductName: '',
  ozelProductVariant: '',
  ozelWidth: '',
  ozelHeight: '',
  ozelUnitPrice: '',
  ozelPileModel: '',
  ozelPileDensity: '',
  ozelUsedM2: '',
  ozelExtraPrice: '',
  ozelQuantity: '',
  ozelBaseUnitPrice: '',
  ozelCapsuleUnitPrice: '',
  ozelCapsulePrice: '',
  ozelTotalPrice: '',
  hasAstar: false,
  astarProductName: '',
  astarProductVariant: '',
  astarUnitPrice: '',
  astarPrice: '',
  jumboSystem: '',
  reductor: '',
  bottomSkirt: '',
  closedCase: '',
  laser: '',
  mounting: '',
  motorType: '',
  motorPrice: '',
  motorPriceCurrency: '',
  isJumbo: false,
  hasAltEtekDilim: false,
  isKapaliKasa: false,
  direction: '',
  pliseItems: [],
  pliseProduct1Name: '',
  pliseProduct1Price: '',
  pliseProduct2Name: '',
  pliseProduct2Price: '',
  pliseProduct1Variation: '',
  pliseProduct2Variation: '',
  pliseCaseVariation: '',
  unitCost: '',
  totalCost: '0.00',
  discount: '',
  birimTipi: 'adet',
  quantity: '',
  hasEgimFarki: false,
  totalPrice: '',
  discountedPrice: '',
  notes: '',
  accessories: [],
  rayOptions: []
});

/**
 * Firebase için veri temizleme (undefined ve gereksiz alanları kaldırır)
 */
export const cleanOrderData = (orderData) => {
  return Object.entries(orderData).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

/**
 * Genel sipariş özetini hesaplar (Ara toplam, iskonto, vade, bakiye vb.)
 */
export const calculateOrderSummary = (orders, genelIskontoYuzde, vadeliYuzde, paidAmount) => {
  // Ara toplam (Ürünlerin toplamı)
  const totalAmount = orders.reduce((sum, order) => sum + (parseFloat(order.totalPrice) || 0), 0);

  // Ürünlerin indirimli toplamı
  const discountedTotalAmount = orders.reduce((sum, order) => {
    if (order.discountedPrice && parseFloat(order.discount) > 0) {
      return sum + parseFloat(order.discountedPrice);
    }
    return sum + (parseFloat(order.totalPrice) || 0);
  }, 0);

  // Genel İskontolu Toplam
  const iskontoluToplam = genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0 
    ? calculateDiscountedPrice(discountedTotalAmount, genelIskontoYuzde) 
    : discountedTotalAmount;

  // Vadeli Toplam
  const vadeliToplam = vadeliYuzde && parseFloat(vadeliYuzde) > 0 
    ? calculateDiscountedPrice(iskontoluToplam, -parseFloat(vadeliYuzde)) 
    : iskontoluToplam;

  // Net Ödenecek Tutar (Genel Toplam)
  const toplamOdeme = vadeliToplam;

  // Kalan Ödeme
  const remainingAmount = Math.max(0, toplamOdeme - (parseFloat(paidAmount) || 0));

  return {
    totalAmount,
    discountedTotalAmount,
    iskontoluToplam,
    vadeliToplam,
    toplamOdeme,
    remainingAmount
  };
};

/**
 * Boş sipariş objesi döndürür (handleAddOrder sonrası temizlik için)
 */
export const getEmptyOrder = () => ({
  roomName: '',
  productName: '',
  productVariant: '',
  width: '',
  height: '',
  pileModel: '',
  pileDensity: '',
  usedM2: '',
  usedM: '',
  usedMeter: '',
  olcuTamamlama: '',
  unitPrice: '',
  extraPrice: '',
  capsuleUnitPrice: '',
  capsulePrice: '',
  jumboSystem: '',
  reductor: '',
  bottomSkirt: '',
  closedCase: '',
  laser: '',
  mounting: '',
  hasOzelTasarim: false,
  ozelProductName: '',
  ozelProductVariant: '',
  ozelWidth: '',
  ozelHeight: '',
  ozelUnitPrice: '',
  ozelPileModel: '',
  ozelPileDensity: '',
  ozelUsedM2: '',
  ozelExtraPrice: '',
  ozelQuantity: '',
  ozelBaseUnitPrice: '',
  ozelCapsuleUnitPrice: '',
  ozelCapsulePrice: '',
  ozelTotalPrice: '',
  hasAstar: false,
  astarProductName: '',
  astarProductVariant: '',
  astarUnitPrice: '',
  astarPrice: '',
  pliseItems: [],
  pliseProduct1Name: '',
  pliseProduct1Price: '',
  pliseProduct2Name: '',
  pliseProduct2Price: '',
  pliseProduct1Variation: '',
  pliseProduct2Variation: '',
  pliseCaseVariation: '',
  backgroundCurtain: '',
  blackout: '',
  crepTul: '',
  multisistem: '',
  honeycomb: '',
  standartPlise: '',
  fabricPrice: '',
  mechanismType: '',
  mechanismPrice: '',
  accessoryPrice: '',
  quantity: '',
  hasEgimFarki: false,
  discount: '',
  totalPrice: '',
  discountedPrice: '',
  notes: '',
  direction: '',
  hasZebraJumbo: false,
  hasZebraAltEtekDilim: false,
  hasZebraKapaliKasa: false,
  zebraJumboAmount: 0,
  zebraAltEtekDilimAmount: 0,
  zebraKapaliKasaAmount: 0,
  accessories: [],
  rayOptions: []
});
